import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    useTheme,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useAppDispatch } from '../../../hooks/reduxHooks'
import CancelInvoiceDialog from './CancelInvoiceDialog'
import { InvoiceManager } from '../../../services/api/InvoiceManager'
import { InvoiceDto, InvoiceIssuerBriefDto, InvoicePaymentType } from '../../../services/api/sharedTypes/Invoice'
import { displayMessage } from '../../../services/redux/snackbar/snackbar-actions'
import TableButtonEdit from '../../table/TableButtonEdit'
import InvoiceServiceSelect from './InvoiceServiceSelect'
import CustomTextField from '../../common/CustomTextField'
import CustomDatePicker from '../../common/CustomDatePicker'
import { getServiceValues } from '../../../utils/invoice-utils'
import InvoicePaymentTypeSelect from '../../common/InvoicePaymentTypeSelect'

interface IssuersDialogProps {
    onSend: (
        invoiceIssuerId: string,
        productsText: string,
        orderNumber: string,
        paymentType: InvoicePaymentType,
        issueDate?: Date,
        taxDate?: Date,
        dueDate?: Date,
    ) => void
    onDelete: (invoiceId: string) => void
    invoice: InvoiceDto
}

const IssuersEditDialog: React.FC<IssuersDialogProps> = ({ onSend, onDelete, invoice }: IssuersDialogProps) => {
    const dispatch = useAppDispatch()
    const theme = useTheme()

    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
    const [loadingPageData, setLoadingPageData] = useState<boolean>(false)
    const [invoiceIssuers, setInvoiceIssuers] = useState<InvoiceIssuerBriefDto[]>([])
    const [selectedInvoiceIssuerId, setSelectedInvoiceIssuerId] = useState<string>(invoice.invoiceIssuer.id)
    const [selectedServiceText, setSelectedServiceText] = useState<string>(invoice.productsText ?? '')
    const [paymentType, setPaymentType] = useState<InvoicePaymentType>(invoice.paymentType ?? InvoicePaymentType.NotSet)
    const [orderNumber, setOrderNumber] = useState<string>(invoice.orderNumber ?? '')
    const [issueDate, setIssueDate] = useState<Date | null>(invoice.issueDate)
    const [dueDate, setDueDate] = useState<Date | null>(invoice.dueDate)
    const [taxDate, setTaxDate] = useState<Date | null>(invoice.taxDate)

    const handleCloseDialog = () => {
        setIsDialogOpen(false)
    }

    const handleOpenDialog = () => {
        setIsDialogOpen(true)
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoadingPageData(true)
            try {
                const invoiceIssuersRes = await InvoiceManager.getManager().getInvoiceIssuerListAsync()
                if (invoiceIssuersRes) {
                    setInvoiceIssuers(invoiceIssuersRes.invoiceIssuers)
                    setSelectedInvoiceIssuerId(invoice.invoiceIssuer.id)
                }
            } catch (error) {
                setLoadingPageData(false)
                return dispatch(displayMessage({ message: error, type: 'error' }))
            }

            setLoadingPageData(false)
        }

        fetchData()
    }, [setLoadingPageData])

    return (
        <>
            <TableButtonEdit onClick={handleOpenDialog} />
            <Dialog open={isDialogOpen} onClose={handleCloseDialog}>
                <DialogTitle textAlign='center'>Chcete změnit fakturu?</DialogTitle>
                <DialogContent>
                    <DialogContentText>Zvolte na koho chcete fakturovat. (Změna změní datum splatnosti)</DialogContentText>
                    {loadingPageData ? (
                        <CircularProgress />
                    ) : invoiceIssuers ? (
                        <FormControl>
                            <RadioGroup
                                value={selectedInvoiceIssuerId}
                                onChange={(event) => {
                                    setSelectedInvoiceIssuerId(event.target.value)
                                    const issuer = invoiceIssuers.find((issuer) => issuer.id === event.target.value)
                                    setDueDate(new Date(new Date().setDate(new Date().getDate() + (issuer?.paymentDays ?? 0))))
                                }}
                            >
                                {invoiceIssuers.map((issuer) => (
                                    <FormControlLabel key={issuer.id} value={issuer.id} control={<Radio />} label={issuer.name} />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    ) : (
                        <div>Žádní vystavitelé faktur</div>
                    )}

                    <DialogContentText>Zvolte popis služby.</DialogContentText>
                    <InvoiceServiceSelect
                        onChange={setSelectedServiceText}
                        oldSelectedServices={invoice.productsText ? getServiceValues(invoice.productsText) : []}
                    />
                    <DialogContentText>Zvolte způsob platby.</DialogContentText>
                    <InvoicePaymentTypeSelect onChange={setPaymentType} value={paymentType} />
                    <DialogContentText>Zvolte číslo objednávky. (Nepovinné)</DialogContentText>
                    <CustomTextField
                        size='small'
                        label='Číslo objednávky (Nepovinné)'
                        fullWidth
                        style={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(2) }}
                        value={orderNumber}
                        onChange={(e) => setOrderNumber(e.target.value)}
                    />

                    <DialogContentText>Zvolte data. (Nechte prázné data co chcete vyplnit automaticky)</DialogContentText>
                    <Stack direction={'row'} spacing={3} style={{ marginTop: theme.spacing(1) }}>
                        <CustomDatePicker onChange={(value) => setIssueDate(value)} value={issueDate} label='Datum vystavení' />
                        <CustomDatePicker onChange={(value) => setDueDate(value)} value={dueDate} label='Datum splatnosti' />
                        <CustomDatePicker onChange={(value) => setTaxDate(value)} value={taxDate} label='Datum zdan. plnění' />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} variant='outlined' style={{ marginRight: theme.spacing(2) }}>
                        Zrušit
                    </Button>
                    <CancelInvoiceDialog onConfirm={() => onDelete(invoice.id)} />
                    <Button
                        onClick={() =>
                            onSend(
                                selectedInvoiceIssuerId,
                                selectedServiceText,
                                orderNumber,
                                paymentType,
                                issueDate ?? undefined,
                                taxDate ?? undefined,
                                dueDate ?? undefined
                            )
                        }
                        variant='contained'
                    >
                        Změnit fakturu
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default IssuersEditDialog
